// src/utils/stars.js
// Star Quest storage (idempotent, unique-per-chapter)
// Eligible gold stars are from Chapters 2..9 (8 total)

const ELIGIBLE_MIN = 2;
const ELIGIBLE_MAX = 9;
const GOAL = ELIGIBLE_MAX - ELIGIBLE_MIN + 1; // 8

// Single source of truth: a map of chapter → 0/1
// We keep compat with both numeric keys and "chapter-N".
const STORE_KEY = "tt_chapterStars_v2";

// Keep your original key so existing players don't lose this flag
const GIZ_SEEN_KEY = "trebleTownGizRevealSeen";

// ----------------- helpers -----------------
function loadMap() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function saveMap(map) {
  localStorage.setItem(STORE_KEY, JSON.stringify(map));
}

function chapterNumFromKey(keyOrNum) {
  // returns a Number (or NaN if no trailing digits)
  const m = String(keyOrNum).match(/(\d+)$/);
  return m ? Number(m[1]) : NaN;
}

function isEligibleChapter(n) {
  return Number.isFinite(n) && n >= ELIGIBLE_MIN && n <= ELIGIBLE_MAX;
}

function dispatchStorageNudge() {
  // lets StarCounter / others refresh immediately
  try {
    window.dispatchEvent(new StorageEvent("storage"));
  } catch {
    // some browsers block direct StorageEvent; a simple custom event also works
    window.dispatchEvent(new Event("storage"));
  }
}

// ----------------- public API -----------------

export function goalStars() {
  return GOAL;
}

/**
 * If called with no args -> returns the raw map object
 * If called with a chapter -> returns 0/1 for that chapter
 */
export function getChapterStars(chapter) {
  const map = loadMap();
  if (chapter === undefined) return map;
  const n = chapterNumFromKey(chapter);
  if (!Number.isFinite(n)) return 0;
  // prefer numeric key, then "chapter-N"
  return Number(map[n] || map[`chapter-${n}`] || 0);
}

/**
 * Idempotent award: sets the chapter to max(prev, value),
 * writes to both numeric and "chapter-N" keys for compatibility.
 * Automatically ignores Chapter 1 and 10 (not eligible).
 */
export function setChapterStars(chapter, value = 1) {
  const n = chapterNumFromKey(chapter);
  if (!Number.isFinite(n)) return 0;

  const map = loadMap();

  // Clamp to 0/1
  const prev = Number(map[n] || map[`chapter-${n}`] || 0) ? 1 : 0;
  const next = Math.max(prev, value ? 1 : 0);

  if (next !== prev) {
    // Always store both shapes so either lookup works
    map[n] = next;
    map[`chapter-${n}`] = next;
    saveMap(map);
    dispatchStorageNudge();
  }

  return next;
}

/**
 * Recomputes unique total from the map:
 * - de-dupes numeric vs "chapter-N"
 * - counts only chapters in [2..9]
 * - counts 1 if the chapter has >=1 star
 */
export function getTotalStars() {
  const map = loadMap();
  const seen = new Set();
  let total = 0;

  for (const k of Object.keys(map)) {
    const n = chapterNumFromKey(k);
    if (!isEligibleChapter(n)) continue;
    if (seen.has(n)) continue;
    const val = Number(map[k] || 0);
    if (val >= 1) {
      total += 1;
      seen.add(n);
    }
  }
  return total;
}

// -------- Gis “reveal seen” flag (kept compatible with your old key) --------
export function hasSeenGizReveal() {
  return localStorage.getItem(GIZ_SEEN_KEY) === "1";
}
export function setSeenGizReveal() {
  localStorage.setItem(GIZ_SEEN_KEY, "1");
}
// alias for convenience if you prefer this name:
export const markGizRevealSeen = setSeenGizReveal;
